import React, { useState } from 'react';
import { 
  Pencil, 
  Copy, 
  Download, 
  FileText, 
  Trash2, 
  Calendar, 
  Phone, 
  CreditCard,
  ChevronDown,
  ChevronUp,
  Shirt,
  Palette,
  Scissors,
  MessageSquare,
  Tag
} from 'lucide-react';

/**
 * ClientCard - Tarjeta de cliente mejorada para móvil
 * Muestra información completa con sección expandible en móvil
 */
const ClientCard = ({ 
  sheet, 
  onEdit, 
  onDuplicate, 
  onExportPDF, 
  onExportPDFInternal, 
  onExportExpediente, 
  onDelete,
  canDuplicate = true,
  canDelete = true,
  canExportExpediente = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isFinalized = sheet.estado === 'finalizada';

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Obtener resumen de prendas
  const getPrendasResumen = () => {
    const prendas = [];
    if (sheet.camisa?.espalda || sheet.camisa?.pecho) prendas.push('Camisa');
    if (sheet.blazer?.espalda || sheet.blazer?.pecho) prendas.push('Blazer');
    if (sheet.pantalon?.largo_total || sheet.pantalon?.cintura) prendas.push('Pantalón');
    if (sheet.chaleco?.pecho || sheet.chaleco?.contorno) prendas.push('Chaleco');
    if (sheet.chaqueta?.espalda || sheet.chaqueta?.manga) prendas.push('Chaqueta');
    return prendas.length > 0 ? prendas.join(', ') : null;
  };

  const prendasResumen = getPrendasResumen();

  // Obtener telas/materiales de las prendas
  const getTelas = () => {
    const telas = [];
    if (sheet.camisa?.tela) telas.push(`Camisa: ${sheet.camisa.tela}`);
    if (sheet.blazer?.tela) telas.push(`Blazer: ${sheet.blazer.tela}`);
    if (sheet.pantalon?.tela) telas.push(`Pantalón: ${sheet.pantalon.tela}`);
    if (sheet.chaleco?.tela) telas.push(`Chaleco: ${sheet.chaleco.tela}`);
    if (sheet.chaqueta_gaban?.tela) telas.push(`Chaqueta: ${sheet.chaqueta_gaban.tela}`);
    return telas.length > 0 ? telas : null;
  };

  // Obtener observaciones de las prendas
  const getObservaciones = () => {
    const obs = [];
    if (sheet.camisa?.observaciones) obs.push(`Camisa: ${sheet.camisa.observaciones}`);
    if (sheet.blazer?.observaciones) obs.push(`Blazer: ${sheet.blazer.observaciones}`);
    if (sheet.pantalon?.observaciones) obs.push(`Pantalón: ${sheet.pantalon.observaciones}`);
    if (sheet.chaleco?.observaciones) obs.push(`Chaleco: ${sheet.chaleco.observaciones}`);
    if (sheet.chaqueta_gaban?.observaciones) obs.push(`Chaqueta: ${sheet.chaqueta_gaban.observaciones}`);
    // También las notas internas generales
    if (sheet.notas_internas) obs.push(`Notas: ${sheet.notas_internas}`);
    return obs.length > 0 ? obs : null;
  };

  const telasInfo = getTelas();
  const observacionesInfo = getObservaciones();

  return (
    <div 
      className="group bg-white rounded-xl md:rounded-2xl border border-warm-100 hover:border-warm-200 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 overflow-hidden"
      data-testid={`sheet-card-${sheet.id}`}
    >
      {/* Header - Siempre visible */}
      <div className="p-5 md:p-6">
        {/* Nombre y Estado */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h3 
              className="font-serif text-xl md:text-2xl text-black leading-tight cursor-pointer hover:text-warm-700 transition-colors"
              onClick={() => onEdit(sheet.id)}
            >
              {sheet.nombre || 'Sin nombre'}
            </h3>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span
                className={`inline-flex items-center px-3 py-1 text-[9px] uppercase tracking-wider font-semibold rounded-full ${
                  isFinalized
                    ? 'bg-gold/10 text-gold-dark border border-gold/30'
                    : 'bg-warm-100 text-warm-600'
                }`}
              >
                {sheet.estado || 'borrador'}
              </span>
              {sheet.cedula && (
                <span className="text-xs text-warm-500 flex items-center gap-1.5">
                  <CreditCard className="w-3 h-3" />
                  {sheet.cedula}
                </span>
              )}
            </div>
          </div>
          
          <button
            onClick={() => onEdit(sheet.id)}
            className="flex items-center gap-2 bg-black text-white hover:bg-warm-800 px-4 py-2.5 text-[10px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shrink-0 shadow-sm hover:shadow-md"
            data-testid={`edit-sheet-${sheet.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>Editar</span>
          </button>
        </div>

        {/* Info Preview - Siempre visible */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* Teléfono */}
          {sheet.telefonos && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-warm-400 shrink-0" />
              <span className="text-warm-700 truncate">{sheet.telefonos}</span>
            </div>
          )}
          
          {/* Fecha Evento */}
          {sheet.fecha_evento && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-warm-400 shrink-0" />
              <span className="text-warm-700">{formatDate(sheet.fecha_evento)}</span>
            </div>
          )}
          
          {/* Prendas */}
          {prendasResumen && (
            <div className="flex items-center gap-2 col-span-2">
              <Shirt className="w-4 h-4 text-warm-400 shrink-0" />
              <span className="text-warm-700 truncate">{prendasResumen}</span>
            </div>
          )}
        </div>

        {/* Botón expandir en móvil */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-2 mt-4 py-2.5 text-xs text-warm-500 hover:text-black md:hidden transition-colors rounded-lg hover:bg-warm-50"
        >
          {isExpanded ? (
            <>
              <span>Ver menos</span>
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              <span>Ver más detalles</span>
              <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* Detalles expandidos en móvil / siempre visible en desktop */}
      <div className={`border-t border-warm-100 ${isExpanded ? 'block' : 'hidden md:block'}`}>
        <div className="p-5 md:p-6 bg-warm-50/50">
          {/* Grid de detalles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Telas / Materiales */}
            {telasInfo && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-warm-100 flex items-center justify-center shrink-0">
                  <Scissors className="w-4 h-4 text-warm-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-warm-500 mb-1 font-medium">Telas</p>
                  <div className="text-sm text-warm-700 space-y-0.5">
                    {telasInfo.slice(0, 2).map((tela, idx) => (
                      <p key={`tela-${idx}-${tela.slice(0,10)}`} className="truncate">{tela}</p>
                    ))}
                    {telasInfo.length > 2 && (
                      <p className="text-warm-400 text-xs">+{telasInfo.length - 2} más</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Forros */}
            {(sheet.blazer?.forro || sheet.chaleco?.forro || sheet.chaqueta_gaban?.forro) && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-warm-100 flex items-center justify-center shrink-0">
                  <Palette className="w-4 h-4 text-warm-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-warm-500 mb-1 font-medium">Forros</p>
                  <p className="text-sm text-warm-700 truncate">
                    {[
                      sheet.blazer?.forro && `Blazer: ${sheet.blazer.forro}`,
                      sheet.chaleco?.forro && `Chaleco: ${sheet.chaleco.forro}`,
                      sheet.chaqueta_gaban?.forro && `Chaqueta: ${sheet.chaqueta_gaban.forro}`
                    ].filter(Boolean).slice(0, 2).join(' | ')}
                  </p>
                </div>
              </div>
            )}

            {/* Evento */}
            {sheet.wedding && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-warm-100 flex items-center justify-center shrink-0">
                  <Tag className="w-4 h-4 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-500 mb-1 font-medium">Evento</p>
                  <p className="text-sm text-warm-700">{sheet.wedding}</p>
                </div>
              </div>
            )}

            {/* Fecha Atención */}
            {sheet.fecha_atencion && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-warm-100 flex items-center justify-center shrink-0">
                  <Calendar className="w-4 h-4 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-500 mb-1 font-medium">Atención</p>
                  <p className="text-sm text-warm-700">{formatDate(sheet.fecha_atencion)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones de prendas */}
          {observacionesInfo && (
            <div className="flex items-start gap-3 pt-4 border-t border-warm-100">
              <div className="w-8 h-8 rounded-lg bg-white border border-warm-100 flex items-center justify-center shrink-0">
                <MessageSquare className="w-4 h-4 text-warm-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-warm-500 mb-1 font-medium">Observaciones</p>
                <div className="text-sm text-warm-600 space-y-1">
                  {observacionesInfo.slice(0, 2).map((obs, idx) => (
                    <p key={`obs-${idx}-${obs.slice(0,10)}`} className="line-clamp-1">{obs}</p>
                  ))}
                  {observacionesInfo.length > 2 && (
                    <p className="text-warm-400 text-xs">+{observacionesInfo.length - 2} más</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="p-4 md:p-5 border-t border-warm-100 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Document Actions */}
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => onExportPDF(sheet.id, sheet.nombre)}
              className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-warm-600 hover:text-black hover:bg-warm-50 border border-transparent hover:border-warm-200 transition-all duration-200 rounded-lg"
              data-testid={`export-pdf-${sheet.id}`}
              title="PDF Cliente"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </button>
            
            <button
              onClick={() => onExportPDFInternal(sheet.id, sheet.nombre)}
              className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-warm-600 hover:text-black hover:bg-warm-50 border border-transparent hover:border-warm-200 transition-all duration-200 rounded-lg"
              data-testid={`export-pdf-internal-${sheet.id}`}
              title="PDF Interno"
            >
              <FileText className="w-4 h-4" />
              <span>Interno</span>
            </button>

            {canExportExpediente && (
              <button
                onClick={() => onExportExpediente(sheet.id, sheet.nombre)}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-gold-dark bg-gold/10 hover:bg-gold/20 border border-gold/30 transition-all duration-200 rounded-lg"
                data-testid={`export-expediente-${sheet.id}`}
                title="Expediente"
              >
                <FileText className="w-4 h-4" />
                <span>Exp.</span>
              </button>
            )}
          </div>

          {/* Secondary Actions */}
          <div className="flex items-center gap-1">
            {canDuplicate && (
              <button
                onClick={() => onDuplicate(sheet.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-warm-500 hover:text-black hover:bg-warm-50 transition-all duration-200 rounded-lg"
                data-testid={`duplicate-sheet-${sheet.id}`}
                title="Duplicar"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}

            {canDelete && (
              <button
                onClick={() => onDelete(sheet.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-warm-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 rounded-lg"
                data-testid={`delete-sheet-${sheet.id}`}
                title="Eliminar"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientCard;
